import { formatMoney } from "@/shared/lib/currency";
import { fromIso8601Date, toLocaleTextAsDate } from "@/shared/lib/datetime";
import { toLocaleNumberText } from "@/shared/lib/number";
import { BaseIcon } from "@/shared/ui/BaseIcons";
import { BaseKeyValue } from "@/shared/ui/BaseKeyValue";
import { CarrierSuggestionBadge } from "@/shared/ui/base-badge";
import { ValueWithLabel } from "@/widgets/value-with-label";
import { Stack, Typography } from "@mui/material";
import type { FreightMatching } from "@x-mile/api-contract";
import { useTranslation } from "react-i18next";

export const OfferCargoCargoInfo = ({
  baseOfferCargo,
  deliveryProposalOfferCargo,
  viewer = "shipper",
}: {
  baseOfferCargo: Pick<
    FreightMatching.schemas.OfferCargo,
    | "shippingFee"
    | "highwayFee"
    | "highwaySection"
    | "fuelSurcharge"
    | "cargoHandlingFee"
    | "othersFee"
    | "adjustmentFee"
    | "paymentDate"
    | "basicFeeMemo"
    | "name"
    | "packingStyle"
    | "count"
    | "weight"
    | "depth"
    | "width"
    | "height"
    | "vehicleWeight"
    | "vehicleShape"
    | "vehicleEquipmentMemo"
    | "memo"
  >;
  deliveryProposalOfferCargo?: Pick<
    FreightMatching.schemas.OfferCargo,
    "vehicle" | "memo"
  >;
  viewer?: "shipper" | "carrier";
}) => {
  const { t } = useTranslation();

  // shippingFeeが0の場合、荷主からの希望運賃がないものとみなす
  const noShipperShippingFeeRequest = baseOfferCargo.shippingFee === 0;

  // 運賃表示の値を決定
  const shippingFeeDisplayValue = (() => {
    if (noShipperShippingFeeRequest) {
      if (viewer === "shipper") {
        return "車主から提案";
      }
      return "提案してください";
    }
    return formatMoney(baseOfferCargo.shippingFee);
  })();

  return (
    <>
      <Typography
        variant="body1"
        fontWeight={700}
        mb={1}
        sx={{
          "::before": {
            content: '"■"',
            fontSize: "150%",
            marginRight: "2px",
          },
        }}
      >
        荷物情報・その他
      </Typography>
      <Stack spacing={0}>
        <BaseKeyValue keyLabel="運賃">
          <ValueWithLabel
            label={t("offerCargo.shippingFee")}
            value={shippingFeeDisplayValue}
            emphasis
          />
          <ValueWithLabel
            label={t("offerCargo.highwayFee")}
            value={baseOfferCargo.highwayFee ? "あり" : "なし"}
            emphasis
          />
          <ValueWithLabel
            label={t("offerCargo.highwaySection")}
            value={baseOfferCargo.highwaySection}
          />
          <ValueWithLabel
            label={t("offerCargo.fuelSurcharge")}
            value={formatMoney(baseOfferCargo.fuelSurcharge)}
          />
          <ValueWithLabel
            label={t("offerCargo.cargoHandlingFee")}
            value={formatMoney(baseOfferCargo.cargoHandlingFee)}
          />
          <ValueWithLabel
            label={t("offerCargo.othersFee")}
            value={formatMoney(baseOfferCargo.othersFee)}
          />
          <ValueWithLabel
            label={t("offerCargo.adjustmentFee")}
            value={formatMoney(baseOfferCargo.adjustmentFee)}
          />
          <ValueWithLabel
            label={t("offerCargo.paymentDate")}
            value={
              baseOfferCargo.paymentDate
                ? toLocaleTextAsDate(
                    fromIso8601Date(baseOfferCargo.paymentDate),
                  )
                : undefined
            }
          />
          <ValueWithLabel
            label={t("offerCargo.basicFeeMemo")}
            value={baseOfferCargo.basicFeeMemo}
          />
        </BaseKeyValue>
        <BaseKeyValue keyLabel="荷物">
          <ValueWithLabel
            label={t("offerCargo.name")}
            value={baseOfferCargo.name}
          />
          <ValueWithLabel
            label={t("offerCargo.packingStyle")}
            value={t(`packingStyle.${baseOfferCargo.packingStyle}`)}
          />
          <ValueWithLabel
            label={t("offerCargo.count")}
            value={toLocaleNumberText(baseOfferCargo.count)}
          />
          <ValueWithLabel
            label={t("offerCargo.weight")}
            value={toLocaleNumberText(baseOfferCargo.weight)}
          />
          <Stack direction="row" alignItems={"center"} spacing={0.5}>
            <Typography variant="body2" color="grey.900">
              {t("size")}
            </Typography>
            <Typography variant="body2" color="grey.900">
              :
            </Typography>
            {!baseOfferCargo.depth &&
            !baseOfferCargo.width &&
            !baseOfferCargo.height ? (
              <Typography variant="body2" color="grey.500">
                未記入
              </Typography>
            ) : (
              <Stack direction="row" alignItems={"center"} spacing={0}>
                <Typography
                  key="size-depth"
                  variant="body2"
                  color={baseOfferCargo.depth ? "grey.900" : "grey.500"}
                >
                  {baseOfferCargo.depth ?? "未記入"}
                </Typography>
                <BaseIcon
                  key="separator-1"
                  name="xClose"
                  sx={{ fontSize: "16px" }}
                />
                <Typography
                  key="size-width"
                  variant="body2"
                  color={baseOfferCargo.width ? "grey.900" : "grey.500"}
                >
                  {baseOfferCargo.width ?? "未記入"}
                </Typography>
                <BaseIcon
                  key="separator-2"
                  name="xClose"
                  sx={{ fontSize: "16px" }}
                />
                <Typography
                  key="size-height"
                  variant="body2"
                  color={baseOfferCargo.height ? "grey.900" : "grey.500"}
                >
                  {baseOfferCargo.height ?? "未記入"}
                </Typography>
                <Typography key="unit" variant="body2" color={"grey.500"}>
                  cm
                </Typography>
              </Stack>
            )}
          </Stack>
        </BaseKeyValue>
        <BaseKeyValue keyLabel="車両">
          <ValueWithLabel
            label={t("offerCargo.vehicleWeight")}
            value={`${toLocaleNumberText(baseOfferCargo.vehicleWeight)}t`}
          />
          <ValueWithLabel
            label={t("offerCargo.vehicleShape")}
            value={t(`vehicleShape.${baseOfferCargo.vehicleShape}`)}
          />
          <ValueWithLabel
            label={t("offerCargo.vehicleEquipmentMemo")}
            value={baseOfferCargo.vehicleEquipmentMemo}
          />
          {deliveryProposalOfferCargo && (
            <ValueWithLabel
              label="装備品"
              value={
                deliveryProposalOfferCargo?.vehicle?.equipments
                  ? deliveryProposalOfferCargo.vehicle.equipments.join(", ")
                  : "未記入"
              }
              fromCarrier
            />
          )}
        </BaseKeyValue>
        <BaseKeyValue keyLabel="その他・特記事項">
          <Typography
            variant="body2"
            color={baseOfferCargo.memo ? "grey.900" : "grey.500"}
          >
            {baseOfferCargo.memo ?? "未記入"}
          </Typography>
          {deliveryProposalOfferCargo && (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <CarrierSuggestionBadge />
              <Typography
                variant="body2"
                color={
                  deliveryProposalOfferCargo.memo ? "grey.900" : "grey.500"
                }
              >
                {deliveryProposalOfferCargo.memo ?? "未記入"}
              </Typography>
            </Stack>
          )}
        </BaseKeyValue>
      </Stack>
    </>
  );
};
